<template>
  <v-container grid-list-xl text-xs-center>
    <ReceiptViewer
      v-if="showDialog"
      ref="receiptDetails"
      inspectMode
      v-on:hide="showDialog = false"
      :showDialog="showDialog"
      :receiptImage="receiptImageElement"
      :receiptImageIsLoading="receiptImageIsLoading"
      :editMode="false"
    />
    <v-layout row column>
      <v-flex xs12>
        <v-card elevation-24 style="padding: 20px">
          <v-card-text>
            <h4 class="display-1 font-weight-bold">Hold On Receipts</h4>
            <p class="mt-3">List of receipts with "Hold On" status</p>
            
            <!-- Filter Section -->
            <v-layout row wrap align-center class="mb-4">
              <v-flex xs12 sm6 md4>
                <v-select
                  v-model="selectedCampaignFilter"
                  :items="campaignFilterOptions"
                  label="Filter by Campaign"
                  clearable
                  outlined
                  dense
                  prepend-inner-icon="mdi-filter"
                  @change="applyFilter"
                ></v-select>
              </v-flex>
            </v-layout>
            
            <!-- Action Buttons -->
            <v-layout row wrap align-center>
              <v-btn
                @click="loadHoldOnReceipts"
                color="grey darken-2"
                large
                :loading="loading"
                class="mr-3"
              >
                <v-icon left>mdi-refresh</v-icon>
                Refresh
              </v-btn>
              <v-btn
                @click="moveToPreConfirmed"
                color="green darken-2"
                large
                :disabled="selectedReceipts.length === 0"
                :loading="movingReceipts"
              >
                <v-icon left>mdi-check-circle</v-icon>
                Move to Pre-Confirmed ({{ selectedReceipts.length }})
              </v-btn>
              <v-spacer></v-spacer>
              <v-chip class="ma-2" color="primary" outlined>
                Total: {{ filteredReceiptsList.length }}
              </v-chip>
            </v-layout>
          </v-card-text>
        </v-card>
      </v-flex>

      <v-flex>
        <v-card>
          <v-data-table
            :items="filteredReceiptsList"
            :no-data-text="loading ? 'Loading...' : 'No hold on receipts found'"
            :disable-pagination="false"
            :items-per-page="10"
            :headers="headers"
            :loading="loading"
            sortBy="createdAt"
            :sort-desc="true"
            show-select
            v-model="selectedReceipts"
            item-key="id"
          >
            <template v-slot:item="{ item, isSelected, select }">
              <tr>
                <td>
                  <v-checkbox
                    :input-value="isSelected"
                    @change="select($event)"
                    color="primary"
                    hide-details
                  ></v-checkbox>
                </td>
                <td @click="setCurrentReceipt(item)" style="cursor: pointer">
                  {{ item.referenceNumber }}
                </td>
                <td @click="setCurrentReceipt(item)" style="cursor: pointer">
                  {{ item.campaignId }}
                </td>
                <td @click="setCurrentReceipt(item)" style="cursor: pointer">
                  <v-chip small color="orange" text-color="white">
                    {{ item.status === "holdOn" ? "HOLD" : item.status }}
                  </v-chip>
                </td>
                <td @click="setCurrentReceipt(item)" style="cursor: pointer">
                  {{ formatDate(item.createdAt) }}
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card>
      </v-flex>
    </v-layout>

    <!-- Confirmation Dialog -->
    <v-dialog v-model="confirmDialog" max-width="500">
      <v-card>
        <v-card-title class="headline">Confirm Action</v-card-title>
        <v-card-text>
          Are you sure you want to move {{ selectedReceipts.length }} receipt(s) to Pre-Confirmed status?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="confirmDialog = false">
            Cancel
          </v-btn>
          <v-btn color="green darken-1" text @click="confirmMoveToPreConfirmed">
            Confirm
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Success Snackbar -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000">
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { mapFields } from "../store/helpers";
import ReceiptViewer from "../components/ReceiptViewer";
import moment from "moment";

export default {
  components: {
    ReceiptViewer,
  },
  data() {
    return {
      loading: false,
      movingReceipts: false,
      showDialog: false,
      receiptImageElement: new Image(),
      receiptImageIsLoading: true,
      receiptsList: [],
      selectedReceipts: [],
      selectedCampaignFilter: null,
      confirmDialog: false,
      snackbar: false,
      snackbarText: "",
      snackbarColor: "success",
      headers: [
        {
          sortable: false,
          text: "Reference Number",
          value: "referenceNumber",
        },
        {
          sortable: true,
          text: "Campaign",
          value: "campaignId",
        },
        {
          sortable: true,
          text: "Status",
          value: "status",
        },
        {
          sortable: true,
          text: "Created At",
          value: "createdAt",
        },
      ],
    };
  },
  computed: {
    ...mapGetters(["userToken", "selectedDashboard"]),
    ...mapFields([
      "reClaimed",
      "reClaimText",
      "referenceNumber",
      "receiptImageURL",
      "createdAt",
      "createdAtDate",
      "createdAtTime",
      "status",
      "id",
      "campaignTitle",
      "campaign_fk",
      "receiptNumber",
      "total",
      "campaignRetailers",
      "hasWallet",
      "products",
      "couponsToCreate",
      "hasEditableCoupons",
      "productImageURL",
      "detailsText",
      "campaignEndDate",
      "campaignStartDate",
      "processedProducts",
      "productTotal",
      "retailerFk",
      "customerToken",
      "selectedProducts",
      "rejectReason",
      "receiptId",
      "vatNumber",
      "paymentAmount",
      "receiptDateTime",
      "country",
      "postCode",
      "clubCode",
      "fiscalId",
      "rppData",
    ]),
    
    // Get unique campaign IDs for filter dropdown
    campaignFilterOptions() {
      const uniqueCampaigns = [...new Set(this.receiptsList.map(r => r.campaignId))];
      return uniqueCampaigns.sort();
    },
    
    // Filter receipts by selected campaign
    filteredReceiptsList() {
      if (!this.selectedCampaignFilter) {
        return this.receiptsList;
      }
      return this.receiptsList.filter(
        receipt => receipt.campaignId === this.selectedCampaignFilter
      );
    }
  },
  methods: {
    ...mapActions([
      "getReceiptsWithConsumerInfo",
      "getReceiptwithRefNumber",
      "getHoldOnReceipts",
      "holdToPreConfirm",
      "getDashboardData",
      "getProductsList",
    ]),

    async loadHoldOnReceipts() {
      this.loading = true;
      try {
        const receipts = await this.getHoldOnReceipts({
          userToken: this.userToken,
          dashboard: this.selectedDashboard,
        });
        this.receiptsList = receipts;
        this.selectedReceipts = [];
        this.selectedCampaignFilter = null; // Reset filter on refresh
      } catch (e) {
        console.error("Failed to load hold on receipts:", e);
        this.receiptsList = [];
        this.showSnackbar("Failed to load receipts", "error");
      } finally {
        this.loading = false;
      }
    },
    
    applyFilter() {
      // Clear selected receipts when filter changes
      this.selectedReceipts = [];
    },

    moveToPreConfirmed() {
      if (this.selectedReceipts.length === 0) {
        this.showSnackbar("Please select at least one receipt", "warning");
        return;
      }
      this.confirmDialog = true;
    },

    async confirmMoveToPreConfirmed() {
      this.confirmDialog = false;
      this.movingReceipts = true;

      try {
        const receiptIds = this.selectedReceipts.map((receipt) => receipt.id);
        
        const result = await this.holdToPreConfirm({
          userToken: this.userToken,
          receiptIds: receiptIds,
          dashboard: this.selectedDashboard,
        });

        if (result.response) {
          this.showSnackbar(
            `Successfully moved ${result.updatedRows} receipt(s) to Pre-Confirmed`,
            "success"
          );
          // Refresh the list
          await this.loadHoldOnReceipts();
        } else {
          this.showSnackbar("Failed to update receipts", "error");
        }
      } catch (e) {
        console.error("Failed to move receipts to pre-confirmed:", e);
        this.showSnackbar(
          e.message || "An error occurred while updating receipts",
          "error"
        );
      } finally {
        this.movingReceipts = false;
      }
    },

    showSnackbar(text, color = "success") {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbar = true;
    },

    formatDate(dateString) {
      return moment(dateString).format("DD-MM-YYYY HH:mm");
    },

    async setCurrentReceipt(item) {
      this.$refs.inspectMode = false;
      await this.getReceiptwithRefNumber({
        userToken: this.userToken,
        referenceNumber: item.referenceNumber,
        dashboard: this.selectedDashboard,
      }).then(async (res) => {
        const receipt = res.receipt;

        if (this.selectedDashboard.isCroppable) {
          this.receiptImageURL = receipt.receiptFullImageURL;
        } else {
          this.receiptImageURL = receipt.receiptImageURL;
        }
        this.productImageURL = receipt.productImageURL;

        const payload = {
          userToken: this.userToken,
          dashboard: this.selectedDashboard,
          campaign_fk: receipt.campaign_fk,
        };
        try {
          await this.getDashboardData(payload);
        } catch (e) {
          console.log("GET DASHBOARD DATA ERROR:: ", e);
        }
        this.img = new Image();
        this.receiptImageIsLoading = true;
        this.img.onload = () => {
          this.paymentAmount = receipt.paymentAmount;
          this.receiptImageIsLoading = false;
          this.receiptImageElement = this.img;
        };

        this.img.src = `${this.receiptImageURL}?${new Date().getTime()}`;
        this.createdAt = receipt.createdAt;
        this.campaignTitle = receipt.campaignTitle;
        this.campaign_fk = receipt.campaign_fk;
        this.receiptDateTime = receipt.receiptDateTime;
        this.createdAtTime = moment(receipt.updatedAt).format("HH:MM");
        this.createdAtDate = receipt.updatedAt;
        this.status = receipt.status;
        this.id = receipt.receiptToken;
        this.customerToken = receipt.customerToken;
        this.referenceNumber = receipt.referenceNumber;
        this.receiptNumber = receipt.identifiedId;
        this.total = receipt.total;
        this.postCode = receipt.postCode;
        this.receiptId = receipt.receiptId;
        this.couponsToCreate = receipt.couponsPerReceipt;
        this.detailsText = receipt.detailsText;
        this.country = receipt.country;
        this.retailerFk =
          receipt.retailer == "15" ? "Not selected" : receipt.retailer;
        if (typeof this.retailerFk === "string") {
          this.retailerFk = parseInt(this.retailerFk);
        }
        this.campaign_fk = receipt.campaignId;
        this.rejectReason = receipt.rejectReason;
        this.paymentAmount = receipt.paymentAmount;
        if (receipt.clubCode) this.clubCode = receipt.clubCode;
        if (receipt.fiscalId) this.fiscalId = receipt.fiscalId;
        if (receipt.rppData) this.rppData = receipt.rppData;
        this.campaignEndDate = moment(receipt.campaignEndDate).format(
          "DD-MM-YYYY"
        );
        this.campaignStartDate = moment(receipt.campaignStartDate).format(
          "DD-MM-YYYY"
        );
        this.hasWallet = receipt.hasWallet;
        if (receipt.selectedProducts) {
          this.selectedProducts = JSON.parse(receipt.selectedProducts);
        }
        this.getProductsList({
          dashboard: this.selectedDashboard,
          campaignId: receipt.campaignId,
        }).then((products) => {
          if (products.length) {
            this.products = products;
            this.processedProducts = [];
            this.showDialog = true;
          } else if (this.selectedDashboard.campaignId === "pg-ro") {
            this.products = products;
            this.processedProducts = [];
            this.showDialog = true;
          }
        });

        if (this.couponsToCreate === -1) {
          this.hasEditableCoupons = true;
          this.couponsToCreate = 1;
        }
        this.campaignRetailers = res.campaignRetailers;
      });
    },
  },
  mounted() {
    if ("name" in this.selectedDashboard === false) {
      const dashboard = JSON.parse(localStorage.selectedDashboard);
      this.$store.commit("setDashboard", dashboard);
    }
    this.$store.commit("setUserRoles", JSON.parse(localStorage.userRoles));

    this.loadHoldOnReceipts();
  },
};
</script>